import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiEdit,
  FiPrinter,
  FiMail,
  FiTruck,
  FiDownload,
  FiCheckCircle,
  FiClock,
  FiSend,
  FiAlertCircle,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const statusStyles = {
  DRAFT: "bg-gray-100 text-gray-700",
  SUBMITTED: "bg-orange-100 text-orange-700",
  APPROVED: "bg-blue-100 text-blue-700",
  PARTIALLY_RECEIVED: "bg-purple-100 text-purple-700",
  RECEIVED: "bg-green-100 text-green-700",
  CLOSED: "bg-green-100 text-green-700",
  CANCELLED: "bg-red-100 text-red-700",
};

export default function PurchaseOrderDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [po, setPo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState("");

  const loadPurchaseOrder = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest(`/purchases/orders/${id}`, {
        accessToken,
      });

      setPo(response);
    } catch (requestError) {
      setError(requestError.message || "Could not load Purchase Order.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken && id) {
      loadPurchaseOrder();
    }
  }, [accessToken, id]);

  const runAction = async (path) => {
    try {
      setIsActionLoading(true);
      setError("");

      await apiRequest(path, {
        method: "POST",
        accessToken,
      });

      await loadPurchaseOrder();
    } catch (requestError) {
      setError(requestError.message || "Purchase Order action failed.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;

  const formatDate = (value) => {
    if (!value) return "—";

    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const totalOrderedQuantity = useMemo(
    () =>
      (po?.items || []).reduce(
        (sum, item) => sum + Number(item.quantityOrdered || 0),
        0,
      ),
    [po],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-800" />
      </div>
    );
  }

  if (!po) {
    return (
      <div className="rounded-3xl border border-red-100 bg-red-50 p-6 text-red-700">
        {error || "Purchase Order not found."}
      </div>
    );
  }

  const canSubmit = po.status === "DRAFT";
  const canApprove = po.status === "SUBMITTED";
  const canCreateGrn =
    po.status === "APPROVED" || po.status === "PARTIALLY_RECEIVED";

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">
            Purchasing / Purchase Orders
          </p>

          <div className="mt-1 flex items-center gap-4">
            <h1 className="text-3xl font-bold">
              {po.purchaseOrderNumber}
            </h1>

            <StatusBadge status={po.status} />
          </div>

          <p className="mt-2 text-gray-500">
            Created on {formatDate(po.createdAt)}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {po.status === "DRAFT" && (
            <ActionButton
              icon={<FiEdit />}
              label="Edit"
              onClick={() =>
                navigate(
                  `/admindashboard/purchases/create?purchaseOrderId=${po.id}`,
                )
              }
            />
          )}

          <ActionButton
            icon={<FiPrinter />}
            label="Print"
            onClick={() => window.print()}
          />

          <ActionButton
            icon={<FiDownload />}
            label="Export"
            onClick={() => window.print()}
          />

          {canSubmit && (
            <button
              type="button"
              disabled={isActionLoading}
              onClick={() =>
                runAction(`/purchases/orders/${po.id}/submit`)
              }
              className="flex h-11 items-center gap-2 rounded-xl bg-blue-600 px-5 text-white disabled:opacity-60"
            >
              <FiSend />
              Submit PO
            </button>
          )}

          {canApprove && (
            <button
              type="button"
              disabled={isActionLoading}
              onClick={() =>
                runAction(`/purchases/orders/${po.id}/approve`)
              }
              className="flex h-11 items-center gap-2 rounded-xl bg-green-600 px-5 text-white disabled:opacity-60"
            >
              <FiCheckCircle />
              Approve PO
            </button>
          )}

          {canCreateGrn && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/admindashboard/purchases/grn/create?purchaseOrderId=${po.id}`,
                )
              }
              className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white"
            >
              <FiTruck />
              Create GRN
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 flex gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <OverviewCard title="Supplier" value={po.supplier?.displayName || "—"} />
        <OverviewCard title="Agreement" value={po.agreementType || 'Legacy — not classified'} />
        <OverviewCard title="Receiving store / branch" value={po.store?.name || "—"} />
        <OverviewCard title="Season" value={po.season?.name || "—"} />
        <OverviewCard title="Expected Delivery" value={formatDate(po.expectedDate)} />
        <OverviewCard title="Total Items" value={totalOrderedQuantity} />
        <OverviewCard title="PO Value" value={formatCurrency(po.totalAmount)} />
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-5 text-lg font-semibold">Supplier Information</h2>

        <div className="grid gap-6 lg:grid-cols-4">
          <Info label="Supplier" value={po.supplier?.displayName} />
          <Info label="Phone" value={po.supplier?.phone} />
          <Info label="Email" value={po.supplier?.email} />
          <Info label="GST Number" value={po.supplier?.gstin} />
        </div>
      </div>

      <div className="mb-6 overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-6">
          <h2 className="text-lg font-semibold">Ordered Products</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left">Product</th>
                <th className="px-6 py-4 text-left">SKU</th>
                <th className="px-6 py-4 text-center">Ordered</th>
                <th className="px-6 py-4 text-center">Received</th>
                <th className="px-6 py-4 text-right">Rate</th>
                <th className="px-6 py-4 text-center">Tax %</th>
                <th className="px-6 py-4 text-right">Amount</th>
              </tr>
            </thead>

            <tbody>
              {po.items.map((item) => (
                <tr key={item.id} className="border-t border-gray-100">
                  <td className="px-6 py-4 font-medium">
                    {item.variant?.product?.name || "—"}
                    {item.variant?.variantName
                      ? ` — ${item.variant.variantName}`
                      : ""}
                  </td>
                  <td className="px-6 py-4">{item.variant?.sku || "—"}</td>
                  <td className="px-6 py-4 text-center">{item.quantityOrdered}</td>
                  <td className="px-6 py-4 text-center">{item.quantityReceived}</td>
                  <td className="px-6 py-4 text-right">{formatCurrency(item.unitPrice)}</td>
                  <td className="px-6 py-4 text-center">{item.taxRate}%</td>
                  <td className="px-6 py-4 text-right font-semibold">{formatCurrency(item.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 lg:col-span-2">
          <h2 className="mb-6 text-lg font-semibold">Activity Timeline</h2>

          <div className="space-y-5">
            <TimelineItem
              title="Purchase Order Created"
              time={formatDate(po.createdAt)}
              icon={<FiCheckCircle />}
            />

            {po.status !== "DRAFT" && (
              <TimelineItem
                title="Purchase Order Submitted"
                time="Submitted for approval"
                icon={<FiSend />}
              />
            )}

            {po.approvedAt && (
              <TimelineItem
                title="Purchase Order Approved"
                time={formatDate(po.approvedAt)}
                icon={<FiCheckCircle />}
              />
            )}

            {po.goodsReceipts?.map((grn) => (
              <TimelineItem
                key={grn.id}
                title={`GRN ${grn.grnNumber} — ${grn.status}`}
                time={formatDate(grn.postedAt || grn.receivedAt)}
                icon={<FiTruck />}
              />
            ))}

            {!po.goodsReceipts?.length && (
              <TimelineItem
                title="Awaiting Delivery"
                time="No GRN created yet"
                icon={<FiClock />}
              />
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-6">
          <h2 className="mb-6 text-lg font-semibold">Order Summary</h2>

          <div className="space-y-4">
            <SummaryRow label="Subtotal" value={formatCurrency(po.subTotal)} />
            <SummaryRow label="Discount" value={`- ${formatCurrency(po.discountAmount)}`} />
            <SummaryRow label="Tax" value={formatCurrency(po.taxAmount)} />
            <SummaryRow label="Freight" value={formatCurrency(po.freightAmount)} />

            <div className="flex justify-between border-t pt-4 text-lg font-bold">
              <span>Total</span>
              <span>{formatCurrency(po.totalAmount)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewCard({ title, value }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <h3 className="mt-2 text-xl font-bold">{value || "—"}</h3>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="mb-1 text-xs text-gray-500">{label}</p>
      <p className="font-medium">{value || "—"}</p>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4"
    >
      {icon}
      {label}
    </button>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`rounded-full px-3 py-1 text-sm font-semibold ${statusStyles[status] || "bg-gray-100 text-gray-700"}`}>
      {status.replaceAll("_", " ")}
    </span>
  );
}

function TimelineItem({ title, time, icon }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
        {icon}
      </div>

      <div>
        <p className="font-medium">{title}</p>
        <p className="text-sm text-gray-500">{time}</p>
      </div>
    </div>
  );
}
